// src/core/storage-arengas.js — capa de Storage para audios de arengas.
//
// FASE 4 · F1.4.a. Sube blobs al bucket público `arengas-audio` siguiendo la
// convención de path {group_id}/{audio_id}.{ext}, y devuelve la URL pública
// vía getPublicUrl (bucket arengas-audio.public = true; ver
// docs/POLICIES_arengas_audio_v2.sql).
//
// Errores expuestos como Error con .code estable:
//   - 'size_exceeded' : el blob supera 5 MB (espejo de audio-recorder).
//   - 'upload_failed' : falla del fetch a Supabase Storage.

import { supabase } from '../supabase.js'
import { extensionForMime } from './audio-recorder.js'

const BUCKET = 'arengas-audio'
const MAX_SIZE_BYTES = 5 * 1024 * 1024
const MAX_RETRIES = 1

const ID_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
const ID_LENGTH = 12

// generateAudioId() — 12 chars URL-safe [A-Za-z0-9].
// Coincide con el espacio que genera crear_arenga server-side (encode base64 +
// translate). El UNIQUE de arengas.audio_id es la garantía final.
export const generateAudioId = () => {
  const length = ID_LENGTH
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(length)
    crypto.getRandomValues(bytes)
    let out = ''
    for (const b of bytes) out += ID_ALPHABET[b % ID_ALPHABET.length]
    return out
  }
  let out = ''
  for (let i = 0; i < length; i++) {
    out += ID_ALPHABET[Math.floor(Math.random() * ID_ALPHABET.length)]
  }
  return out
}

export const getMaxSize = () => MAX_SIZE_BYTES

// Link público compartible — ruta canónica pactomundial.futbol/t/{audio_id}.
// El router de la app intercepta /t/{audio_id} y abre el modo visitante de
// tribuna (FASE 3.17 Bloque D). Dominio centralizado en core/constants.js
// (cierra DT-CK, 23-may noche).
export { buildArengaShareUrl as buildShareUrl } from './constants.js'

const createUploadError = (code, message, cause) => {
  const e = new Error(message || code)
  e.code = code
  if (cause !== undefined) e.cause = cause
  return e
}

// buildPath(groupId, audioId, mimeType) — devuelve `{group_id}/{audio_id}.{ext}`.
export const buildPath = (groupId, audioId, mimeType) => {
  if (!groupId || !audioId) throw createUploadError('bad_path', 'group_id + audio_id requeridos.')
  return `${groupId}/${audioId}.${extensionForMime(mimeType)}`
}

// getPublicUrl(path) — devuelve la URL pública del CDN.
export const getPublicUrl = (path) => {
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path)
  return data?.publicUrl || ''
}

// uploadArenga(blob, mimeType, groupId, audioId) — sube y devuelve { path, publicUrl }.
// Reintenta UNA vez si el upload falla por red (no por validación).
export const uploadArenga = async (blob, mimeType, groupId, audioId) => {
  if (!blob || typeof blob.size !== 'number') {
    throw createUploadError('upload_failed', 'Blob inválido.')
  }
  if (blob.size > MAX_SIZE_BYTES) {
    throw createUploadError('size_exceeded', `El audio supera ${MAX_SIZE_BYTES} bytes.`)
  }
  const path = buildPath(groupId, audioId, mimeType)
  const contentType = (mimeType || '').split(';')[0].trim() || 'application/octet-stream'

  let attempt = 0
  let lastErr = null
  while (attempt <= MAX_RETRIES) {
    // upsert:false a propósito (F1.4.a): el path lleva audio_id random
    // URL-safe de 12 chars → colisión ~1/62^12. Si dejamos upsert:true, el
    // header x-upsert obliga a tener policy UPDATE además de INSERT; sin
    // ella, RLS rechaza con 403 incluso cuando el path no existe.
    const { error } = await supabase.storage.from(BUCKET).upload(path, blob, {
      contentType,
      upsert: false,
    })
    if (!error) {
      return { path, publicUrl: getPublicUrl(path) }
    }
    lastErr = error
    attempt += 1
  }
  throw createUploadError('upload_failed', lastErr?.message || 'No pudimos subir el audio.', lastErr)
}

// removeArenga(path) — borra el archivo. Solo se llama desde el frontend cuando
// el RPC eliminar_arenga no estuvo disponible (defensa en profundidad); por
// default el RPC borra el objeto en la misma transacción.
export const removeArenga = async (path) => {
  if (!path) return { ok: true }
  const { error } = await supabase.storage.from(BUCKET).remove([path])
  if (error) return { error }
  return { ok: true }
}
